import { useMemo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import type { TrendForecastPoint, TrendSeriesPoint } from "@chessla/shared";
import { ChesslaChart } from "./ChesslaChart";
import { buildContinuousTrendOption } from "./chart-options";

interface ForecastView {
  today: string;
  calibration: { holdoutSize: number; coverage: number };
  series: TrendForecastPoint[];
}
const date = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" });
const number = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));
export const trendPointLabel = (point: TrendSeriesPoint, valueLabel: string, index: number, total: number): string =>
  `${date.format(new Date(point.playedAt))}. ${valueLabel} ${number(point.value)}. Game ${index} of ${total}. Open game analysis.`;

export const TrendLine = ({
  series,
  label,
  valueLabel,
  forecast,
}: {
  series: TrendSeriesPoint[];
  label: string;
  valueLabel: string;
  forecast?: ForecastView;
}) => {
  const navigate = useNavigate();
  const option = useMemo(
    () => buildContinuousTrendOption({ series, label, valueLabel, forecast }),
    [forecast, label, series, valueLabel],
  );
  if (series.length === 0)
    return <Typography sx={{ color: "text.secondary" }}>No eligible games in this scope.</Typography>;
  const forecastLabel = forecast
    ? ` and forecast with Nominal model range (not a guarantee). Measured held-out coverage ${Math.round(forecast.calibration.coverage * 100)}% (${forecast.calibration.holdoutSize} games)`
    : "";
  return (
    <Box sx={{ minWidth: 0, overflow: "hidden" }}>
      <ChesslaChart
        kind="continuous-trend"
        label={`${label}. Observed games${forecastLabel}. Hover or touch for date and value; exact games follow below.`}
        option={option}
        height="clamp(270px,32vw,390px)"
        onGameSelect={(gameId) => void navigate(`/games/${encodeURIComponent(gameId)}`)}
      />
      <Box
        component="details"
        sx={{
          mt: 1,
          color: "text.secondary",
          fontSize: 12,
          "& summary": {
            width: "fit-content",
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: "primary.main",
            fontWeight: 800,
            letterSpacing: ".08em",
            textTransform: "uppercase",
          },
        }}
      >
        <summary>View exact game data</summary>
        <Box component="ol" sx={{ m: 0, pl: 2.5, columns: { md: 2 }, columnGap: 4 }}>
          {series.map((point, index) => (
            <li key={point.gameId}>
              <Link
                component={RouterLink}
                to={`/games/${encodeURIComponent(point.gameId)}`}
                sx={{ minHeight: 44, display: "inline-flex", alignItems: "center", color: "primary.main" }}
              >
                {trendPointLabel(point, valueLabel, index + 1, series.length)}
              </Link>
            </li>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
